import { distanceKm } from '../lib/geo';
import { groupedBy, pileOf } from '../lib/grouped';
import { placePerson } from './piles';
import type { Location, Person, PersonPlace, UUID } from './types';

/**
 * Where people are. A person has a home (`Person.homeLocationId`) and a
 * record of places (`PersonPlace` rows): every home they have had, and every
 * visit with dates. On any given day the person is at their active visit if
 * there is one, otherwise at home.
 */

export interface Whereabouts {
  locationId: UUID;
  kind: 'home' | 'visit';
  /** The visit row when `kind` is 'visit'. */
  place?: PersonPlace;
}

/** Today as YYYY-MM-DD in the given IANA time zone (device zone when omitted or unknown). */
export function localDateISO(now: Date = new Date(), timezone?: string): string {
  try {
    const fmt = new Intl.DateTimeFormat('en-CA', { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit' });
    // en-CA formats as YYYY-MM-DD
    return fmt.format(now);
  } catch {
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
}

export function isValidDateISO(s: string | undefined): s is string {
  return !!s && /^\d{4}-\d{2}-\d{2}$/.test(s) && !Number.isNaN(Date.parse(`${s}T00:00:00Z`));
}

/** Days from `a` to `b` (YYYY-MM-DD), positive when b is later. */
export function daysBetween(a: string, b: string): number {
  return Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86_400_000);
}

export function addDays(date: string, days: number): string {
  const d = new Date(Date.parse(`${date}T00:00:00Z`) + days * 86_400_000);
  return d.toISOString().slice(0, 10);
}

/** Is `date` inside [from, to] (inclusive; open-ended when `to` is absent)? */
export function placeCovers(place: Pick<PersonPlace, 'from' | 'to' | 'cancelled'>, date: string): boolean {
  if (place.cancelled) return false;
  if (date < place.from) return false;
  if (place.to && date > place.to) return false;
  return true;
}

/** The visit a person is on for `date`, if any (latest-starting wins when visits overlap). */
export function activeVisit(places: PersonPlace[], personId: UUID, date: string): PersonPlace | undefined {
  let best: PersonPlace | undefined;
  // Their own few, not the family's whole record — this is asked for every person, every minute (Shabbat, the globe, the tree).
  for (const p of pileOf(groupedBy(places, placePerson), personId)) {
    if (p.kind !== 'visit' || !placeCovers(p, date)) continue;
    if (!best || p.from > best.from || (p.from === best.from && p.createdAt > best.createdAt)) best = p;
  }
  return best;
}

/** Where a person is on `date`: an active visit, else their home. */
export function whereIsAt(person: Pick<Person, 'id' | 'homeLocationId'>, places: PersonPlace[], date: string): Whereabouts | null {
  const visit = activeVisit(places, person.id, date);
  if (visit) return { locationId: visit.locationId, kind: 'visit', place: visit };
  // A home that was recorded for that day (past homes matter for history)
  let home: PersonPlace | undefined;
  for (const p of pileOf(groupedBy(places, placePerson), person.id)) {
    if (p.kind !== 'home' || !placeCovers(p, date)) continue;
    if (!home || p.from > home.from) home = p;
  }
  if (home) return { locationId: home.locationId, kind: 'home' };
  if (person.homeLocationId) return { locationId: person.homeLocationId, kind: 'home' };
  return null;
}

/** Visits that have not ended yet, soonest first (the current one first when there is one). */
export function upcomingVisits(places: PersonPlace[], personId: UUID, date: string): PersonPlace[] {
  return places
    .filter((p) => p.personId === personId && p.kind === 'visit' && !p.cancelled && (!p.to || p.to >= date))
    .sort((a, b) => a.from.localeCompare(b.from));
}

/** The whole record for a person, newest first — homes and visits, cancelled ones included. */
export function placeHistory(places: PersonPlace[], personId: UUID): PersonPlace[] {
  return places.filter((p) => p.personId === personId).sort((a, b) => b.from.localeCompare(a.from) || b.createdAt.localeCompare(a.createdAt));
}

/** Visit dates must be a real range, not absurdly long, and not entirely in the distant past. */
export function validateVisit(from: string, to: string, today: string): 'ok' | 'invalidDate' | 'endBeforeStart' | 'tooLong' | 'inPast' {
  if (!isValidDateISO(from) || !isValidDateISO(to)) return 'invalidDate';
  if (to < from) return 'endBeforeStart';
  if (daysBetween(from, to) > 366) return 'tooLong';
  if (to < addDays(today, -365)) return 'inPast';
  return 'ok';
}

/** The person's own time zone: from where they are today, else undefined. */
export function timezoneOf(person: Pick<Person, 'id' | 'homeLocationId'>, places: PersonPlace[], locations: Map<UUID, Location>, date: string): string | undefined {
  const w = whereIsAt(person, places, date);
  return w ? locations.get(w.locationId)?.timezone : undefined;
}

export function locationLabel(loc: Location | undefined, lang: 'en' | 'he'): string {
  if (!loc) return '';
  return lang === 'he' ? loc.labelHe || loc.label : loc.label;
}

/** How close counts as "nearby" for a visit: the same city or its surroundings. */
export const NEARBY_KM = 60;

/**
 * People who are at (or near) a place on a date — by their home or an active
 * visit of their own — with how far away they are. Used to tell relatives when
 * someone is coming to town.
 */
export function peopleNear(
  target: Pick<Location, 'lat' | 'lng'>,
  people: Pick<Person, 'id' | 'homeLocationId' | 'status' | 'archived' | 'mergedIntoId'>[],
  places: PersonPlace[],
  locations: Map<UUID, Location>,
  date: string,
  km = NEARBY_KM,
): Array<{ personId: UUID; km: number }> {
  if (target.lat === undefined || target.lng === undefined) return [];
  const out: Array<{ personId: UUID; km: number }> = [];
  for (const p of people) {
    if (p.archived || p.mergedIntoId || p.status === 'deceased') continue;
    const w = whereIsAt(p, places, date);
    const loc = w ? locations.get(w.locationId) : undefined;
    if (!loc || loc.lat === undefined || loc.lng === undefined) continue;
    const d = distanceKm(target.lat, target.lng, loc.lat, loc.lng);
    if (d <= km) out.push({ personId: p.id, km: Math.round(d) });
  }
  return out.sort((a, b) => a.km - b.km);
}
